import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, InjectionToken } from "@angular/core";
//import { plural } from "pluralize";
import { Observable, of, throwError } from "rxjs";
import { map, concatMap, catchError } from "rxjs/operators";
import { ODataParams } from "./odata-params";
import { Query } from "./query";
import { ODataType } from "./odata-type";
import { IBatchDataSet } from "./batch-data";
import { toCamelCase, toPascalCase } from "../tools";
import "reflect-metadata";
//import * as pluralize from 'pluralize';


export class ODataConfiguration {
  host: string = "";
  routePrefix: string = "odata";
}

export const ODATA_CONFIG = new InjectionToken<ODataConfiguration>("odata-configuration");

@Injectable({
  providedIn: "root",
})
export class ODataService {
  private host: string = "";
  private batchRoute: string = "odata/$batch";

  constructor(@Inject(ODATA_CONFIG) private configuration: ODataConfiguration, private http: HttpClient) {
    this.host = `${configuration.host}/${configuration.routePrefix}`;
    this.batchRoute = `$batch`;
  }

  get<TEntity extends Object>(
    entity: string | TEntity | null,
    arg: { params: ODataParams; route?: string; skipCamelCaseTransformResponse?: boolean; skipPluralize?: boolean; loadAllRecursive?: boolean }
  ): Observable<any> {
    let query: string = Query.build(arg.params);
    query = arg.params.queryParams ? query + "&" + arg.params.queryParams.toString() : query;
    let url: string =
      entity != null
        ? `${this.host}${entity ? '/' : ''}${arg.route ? arg.route : this.pathBuilder(entity!, arg.skipPluralize)}${query}`
        : `${this.host}${query}`;

    return this.http
      .get(url)
      .pipe(
        concatMap((response: any) =>
          arg.loadAllRecursive && response['@odata.nextLink'] && response["value"] && Array.isArray(response["value"])
            ? this.getAllRecursive(response, response['@odata.nextLink']).pipe(map(
                (oo: any) => arg.skipCamelCaseTransformResponse ? oo["value"] : toCamelCase(oo["value"])
              ))
            : of(arg.skipCamelCaseTransformResponse ? response["value"] ?? response : toCamelCase(response["value"] ?? response))
        )
      );
  }

  post<TEntity extends Object>(
    entity: string | TEntity,
    arg: { params: ODataParams; body: Object; route?: string; skipPascalCaseTransform?: boolean; skipPluralize?: boolean }
  ): Observable<any> {
    let query: string = Query.build(arg.params);
    let url: string = `${this.host}/${arg.route ? arg.route : this.pathBuilder(entity, arg.skipPluralize)}${query}`;

    return this.http
      .post(url, arg.skipPascalCaseTransform ? arg.body : toPascalCase(arg.body), {
        headers: { Prefer: "return=representation" },
      })
      .pipe(map((response: any) => toCamelCase(response)));
      // .pipe(map((response: any) => toCamelCase(response)), 
      //   catchError((e: any) => { 
      //     console.error('ODataService: post: e: ', e); 
      //     return throwError(() => e); }));
  }

  patch<TEntity extends Object>(
    entity: string | TEntity,
    arg: { params?: ODataParams; key?: string; body?: Object; route?: string; skipPluralize?: boolean }
  ): Observable<any> {
    let query: string = arg.key
      ? Query.build({ key: { type: ODataType.guid, value: arg.key } })
      : arg.params
      ? Query.build(arg.params)
      : "";
    let url: string = `${this.host}/${arg.route ? arg.route : this.pathBuilder(entity, arg.skipPluralize)}${query}`;

    return this.http
      .patch(url, toPascalCase(arg.body), {
        headers: { Prefer: "return=representation" },
      })
      .pipe(map((response: any) => toCamelCase(response)));
  }

  delete<TEntity extends Object>(
    entity: string | TEntity,
    arg: { params?: ODataParams; key?: string; route?: string; skipPluralize?: boolean }
  ): Observable<any> {
    let query: string = arg.key
      ? Query.build({ key: { type: ODataType.guid, value: arg.key } })
      : arg.params
      ? Query.build(arg.params)
      : "";
    let url: string = `${this.host}/${arg.route ? arg.route : this.pathBuilder(entity, arg.skipPluralize)}${query}`;

    return this.http
      .delete(url, {
        headers: { Prefer: "return=representation" },
      })
      .pipe(map((response: any) => toCamelCase(response)));
  }

  list<TEntity>(entity: string | TEntity, arg: { params: ODataParams; route?: string; skipPluralize?: boolean, countField?: string; loadAllRecursive?: boolean }): Observable<any> {
    let query: string = Query.build(arg.params);
    query = arg.params.queryParams ? query + "&" + arg.params.queryParams.toString() : query;

    let url: string = `${this.host}${entity ? '/' : ''}${arg.route ? arg.route : this.pathBuilder(entity, arg.skipPluralize)}${query}`;

    return this.http.get(url).pipe(
      concatMap(
        (response: any) =>
          {
            if (arg.loadAllRecursive && response['@odata.nextLink'] && response["value"] && Array.isArray(response["value"])) {
              return this.getAllRecursive(response, response['@odata.nextLink']).pipe(map((oo: any) => {
                return <any>{
                  data: toCamelCase(oo["value"]),
                  total: parseInt(oo[arg.countField ?? "@odata.count"], 10),  
                }
              }));
            } else {
              return of(<any>{
                data: toCamelCase(response["value"]),
                total: parseInt(response[arg.countField ?? "@odata.count"], 10),
              });
            }
          }
      )
    );
  }

  batch(...requests: IBatchDataSet[]): Observable<any> {
    var changeSet = { requests: new Array<IBatchDataSet>() };

    requests.forEach((req, index) => {
      if (!req.url) {
        req.url = `${this.pathBuilder(req.type)}`;
      }
      if (req.id) {
        req.url = req.url + Query.build({ key: { type: ODataType.guid, value: req.id } });
      }

      changeSet.requests.push({
        id: `${index++}`,
        method: req.method,
        headers: { Prefer: "return=representation" },
        url: req.url,
        body: req.body ? toPascalCase(req.body) : {},
      });
    });

    return this.http.post(`${this.host}/${this.batchRoute}`, changeSet, { responseType: "text" });
  }

  private pathBuilder<TEntity>(entity: string | TEntity, skipPluralize?: boolean): string {
    let entityName = entity instanceof Object ? Reflect.getMetadata("entity:name", entity) : entity;
    //if (entityName) return skipPluralize ? entityName : plural(entityName);
    if (entityName) return entityName;
    return "";
  }
  
  private getAllRecursive(result: any, nextLink: string): Observable<any> {
    return this.http.get(nextLink).pipe(concatMap((o: any) => {
      result.value.push(...o.value);
      return o['@odata.nextLink'] ? this.getAllRecursive(result, o['@odata.nextLink']) : of(result);
    }))
  }

}
